import MainLayout from "../../layout/MainLayout";
import { Button1 } from "../index";
import refreshIcon from "../../assets/icons/refresh-icon.png";
import downIcon from "../../assets/icons/chevron-down.png";
import circle from "../../assets/icons/Ellipse 81.png";
import editIcon from "../../assets/icons/edit-icon.png";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useBeneficiaries } from "../../data/live";
import {
  scheduledTransfersApi,
  transfersApi,
} from "../../api/endpoints";
import { newIdempotencyKey } from "../../utils/idempotency";
import TransferOtpModal from "../modals/TransferOtpModal";

const DARK_GREEN = "#0E2C24";
const BRAND_GREEN = "#1BBB6B";
const LABEL_GRAY = "#7D7D7D";
const BORDER_GRAY = "#E3E3E3";

const fmt = (n, c) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${c || ""}`.trim();

const ReviewScheduleTransfer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { beneficiaries } = useBeneficiaries();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [idempotencyKey] = useState(newIdempotencyKey);

  const state = location.state || {};
  const { form = {}, isScheduled, sourceAccount, quote } = state;

  const beneficiary = beneficiaries.find((b) => b.id === form.beneficiaryId);
  const sourceCurrency = sourceAccount?.currency || "AUD";

  const bankDetails = [
    {
      name: "Bank Name",
      info: beneficiary?.bankAccounts?.[0]?.bankName || "-",
    },
    {
      name: "Swift Code",
      info: beneficiary?.bankAccounts?.[0]?.swiftCode || "-",
    },
    {
      name: "Account number",
      info: beneficiary?.bankAccounts?.[0]?.iban || "-",
    },
    { name: "Reference", info: form.referenceNote || "-" },
    { name: "Purpose", info: form.purpose || "-" },
    {
      name: "Transfer date",
      info: form.scheduleDate
        ? new Date(form.scheduleDate).toLocaleDateString()
        : "Today",
    },
  ];

  const scheduleDetails = [
    {
      name: "Start date",
      info: form.scheduleDate
        ? new Date(form.scheduleDate).toLocaleDateString()
        : "-",
    },
    { name: "Repeats", info: form.frequency || "ONCE" },
    {
      name: "End date",
      info: form.endDate ? new Date(form.endDate).toLocaleDateString() : "-",
    },
  ];

  const additionalDetails = [
    { name: "Category", info: form.category || "-" },
  ];

  const recipientName =
    beneficiary?.fullName || beneficiary?.businessName || "-";

  const renderRow = ({ name, info }) => (
    <div
      key={name}
      className="d-flex align-items-start justify-content-between gap-3"
    >
      <span className="fs12px" style={{ color: LABEL_GRAY }}>
        {name}
      </span>
      <span className="fs12px text-end" style={{ color: "#161616" }}>
        {info}
      </span>
    </div>
  );

  // "Schedule Transfer" goes straight through (money moves later, via the
  // worker); "Send Now" requires the emailed OTP/PIN step first.
  const handleConfirm = () => {
    setError(null);
    if (isScheduled && form.scheduleDate) {
      handleSubmit();
    } else {
      setOtpOpen(true);
    }
  };

  const handleSubmit = async ({ otpCode, pinCode } = {}) => {
    setError(null);
    setSubmitting(true);
    try {
      if (isScheduled && form.scheduleDate) {
        await scheduledTransfersApi.create({
          sourceAccountId: form.sourceAccountId,
          sourceAmount: Number(form.sourceAmount),
          destCurrency: form.destCurrency,
          transferType: "INTERNATIONAL",
          beneficiaryId: form.beneficiaryId,
          category: form.category || undefined,
          referenceNote: form.referenceNote || undefined,
          startDate: form.scheduleDate,
          frequency: form.frequency || "ONCE",
          endDate: form.endDate || undefined,
        });
      } else {
        await transfersApi.create({
          sourceAccountId: form.sourceAccountId,
          sourceAmount: Number(form.sourceAmount),
          destCurrency: form.destCurrency,
          transferType: "INTERNATIONAL",
          beneficiaryId: form.beneficiaryId,
          category: form.category || undefined,
          referenceNote: form.referenceNote || undefined,
          otpCode,
          pinCode,
        }, idempotencyKey);
      }
      setOtpOpen(false);
      navigate("/my-transaction");
    } catch (e) {
      // OTP/PIN problems stay inside the modal so the user can retry/resend.
      const apiErr = e.response?.data?.error || "";
      if (apiErr.startsWith("OTP_") || apiErr.startsWith("PIN_")) throw e;
      setOtpOpen(false);
      setError(
        e.response?.data?.error ||
          e.response?.data?.details?.[0]?.message ||
          e.message ||
          "Schedule failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout
      NavName="Schedule Transfer"
      expand={false}
      pageClassName="dashboard-page schedule-transfer-page"
      contentClassName="dashboard-content"
    >
      <div className="schedule-transfer-shell">
          <div className="schedule-transfer-overview">
            <div>
              <span className="dashboard-eyebrow">Final review</span>
              <h2>Review Schedule Transfer</h2>
              <p>Confirm recipient, schedule, amount, and transfer details before submitting.</p>
            </div>
          </div>

          <div
            className="schedule-transfer-review-card rounded-3 p-4 mt-4"
            style={{ border: `1px solid ${BORDER_GRAY}` }}
          >
            <div className="d-flex justify-content-end mb-2">
              <div className="d-flex flex-column align-items-end gap-1">
                <div className="d-flex align-items-center gap-2">
                  <span className="fs12px" style={{ color: LABEL_GRAY }}>
                    Exchange rate |
                  </span>
                  <span className="fs12px" style={{ color: LABEL_GRAY }}>
                    {quote ? "Live" : "No quote"}
                  </span>
                  <img
                    src={refreshIcon}
                    alt="refresh"
                    width={18}
                    height={18}
                    className="cursor-pointer"
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="fs14px fw-semibold">
                    1 {sourceCurrency} ={" "}
                    <span style={{ color: BRAND_GREEN }}>
                      {quote?.exchangeRate || "0.00"} {form.destCurrency}
                    </span>
                  </span>
                  <img src={downIcon} alt="down" className="cursor-pointer" />
                </div>
              </div>
            </div>

            <h5 className="fw-medium mb-4" style={{ color: "#161616" }}>
              Review Your Transfer
            </h5>

            <div className="row g-4 mb-4">
              <div className="col-md-6">
                <div className="d-flex flex-column gap-2 mb-3">
                  <span className="fs12px" style={{ color: LABEL_GRAY }}>
                    Amount you want to send
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-6 fw-medium">
                      {fmt(form.sourceAmount, sourceCurrency)}
                    </span>
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  <span className="fs12px" style={{ color: LABEL_GRAY }}>
                    Transaction fees
                  </span>
                  <span className="fs-6 fw-medium">
                    {fmt(quote?.fees || 0, sourceCurrency)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="d-flex flex-column gap-2">
                  <span className="fs12px" style={{ color: LABEL_GRAY }}>
                    From account
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <img src={circle} alt="" width={24} height={24} />
                    <span className="fs-6 fw-medium">
                      {sourceCurrency} ****
                      {(sourceAccount?.iban || "0000").slice(-4)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-lg-6">
                <div
                  className="schedule-transfer-review-panel rounded-3 p-4 h-100"
                  style={{ border: `1px solid ${BORDER_GRAY}` }}
                >
                  <div className="d-flex flex-column gap-2 mb-3">
                    <span className="fs12px" style={{ color: LABEL_GRAY }}>
                      Recipients get
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fs-6 fw-medium">
                        {fmt(quote?.destAmount || 0, form.destCurrency)}
                      </span>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2 mb-3">
                    <span className="fs12px" style={{ color: LABEL_GRAY }}>
                      Recipients
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <img src={circle} alt="" width={38} height={38} />
                      <div className="d-flex flex-column">
                        <span className="fs-6 fw-medium">
                          {recipientName}
                        </span>
                        <span
                          className="fs12px"
                          style={{ color: LABEL_GRAY }}
                        >
                          {beneficiary?.countryCode || form.destCountry}
                        </span>
                      </div>
                    </div>
                  </div>

                  <hr className="opacity-25" />

                  <div className="d-flex flex-column gap-3">
                    {bankDetails.map(renderRow)}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="d-flex flex-column gap-3 h-100">
                  <div
                    className="schedule-transfer-review-panel rounded-3 p-4"
                    style={{ border: `1px solid ${BORDER_GRAY}` }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fs12px" style={{ color: LABEL_GRAY }}>
                        Schedule
                      </span>
                      <img
                        src={editIcon}
                        alt="edit"
                        className="cursor-pointer"
                        onClick={() => navigate("/schedule-transfer")}
                      />
                    </div>
                    <hr className="opacity-25" />
                    <div className="d-flex flex-column gap-3">
                      {scheduleDetails.map(renderRow)}
                    </div>
                  </div>

                  <div
                    className="schedule-transfer-review-panel rounded-3 p-4"
                    style={{ border: `1px solid ${BORDER_GRAY}` }}
                  >
                    <span className="fs12px" style={{ color: LABEL_GRAY }}>
                      Additional Details
                    </span>
                    <hr className="opacity-25" />
                    <div className="d-flex flex-column gap-3">
                      {additionalDetails.map(renderRow)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-3">
                <span className="fs12px" style={{ color: "#FC555B" }}>
                  {error}
                </span>
              </div>
            )}

            <div className="d-flex justify-content-end align-items-center flex-wrap gap-3 mt-4">
              <Button1
                buttonName={"Previous"}
                className={"fs14px"}
                style={{
                  border: `1px solid ${BORDER_GRAY}`,
                  color: "#303030",
                }}
                onClick={() => navigate("/schedule-transfer")}
              />
              <Button1
                buttonName={"Save as Draft"}
                className={"fs14px"}
                style={{
                  border: `1px solid ${BRAND_GREEN}`,
                  color: BRAND_GREEN,
                  background: "transparent",
                }}
              />
              <Button1
                buttonName={
                  submitting
                    ? "Submitting..."
                    : isScheduled
                    ? "Schedule Transfer"
                    : "Send Now"
                }
                className="fs14px text-white"
                style={{ background: DARK_GREEN, border: "none" }}
                onClick={handleConfirm}
                disabled={submitting}
              />
            </div>
        </div>
      </div>

      <TransferOtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        onVerify={handleSubmit}
      />
    </MainLayout>
  );
};

export default ReviewScheduleTransfer;
